import { ChevronUp, CircleCheck, Minus, TriangleAlert, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  useDispatch,
  useSelector as useReduxSelector,
  useSelector
} from "react-redux";
import {
  filterDocumentsBySupport,
  type PlaceholderFile
} from "../../helpers/file_helpers";
import { documentSelectors } from "../../redux/documents/selectors";
import {
  setExpanded,
  setFiles,
  setVisibilty
} from "../../redux/file-upload/fileUploadSlice";
import { uploadFileSelectors } from "../../redux/file-upload/selectors";
import CustomTooltip from "./CustomTooltip";

export const FileUploadWindow = ({
  path,
  id
}: {
  path: string;
  id: string;
}) => {
  const getFileDisplayName = (file: File) => {
    const placeholderFile = file as PlaceholderFile;
    return placeholderFile.placeholderDisplayName ?? file.name;
  };

  const dispatch = useDispatch();
  const {
    files,
    error,
    isLoading,
    uploadProgress,
    visible,
    expanded: isExpanded,
    fileErrors,
    uploadConfiguration
  } = useSelector(uploadFileSelectors.selectAll);

  const uploadProgressValues = Object.values(uploadProgress);
  const uploading =
    uploadProgressValues.length > 0 &&
    uploadProgressValues.reduce((acc, currentValue) => acc + currentValue, 0) %
      uploadProgressValues.length !==
      0;

  const corruptRemovedNames = useReduxSelector(
    documentSelectors.corruptRemovedNames
  ) as string[];

  const filesWithSpecificErrors = files.filter((f) => fileErrors[f.name]);

  const corruptedFiles = files.filter(
    (f) => f.size === 0 && !fileErrors[f.name]
  );
  const supportedFiles = filterDocumentsBySupport(
    files.filter((f) => f.size > 0),
    true
  );
  const errorNameSet = new Set(Object.keys(fileErrors || {}));
  const visibleSupportedFiles = supportedFiles.filter(
    (f) => !errorNameSet.has(f.name)
  );
  const unsupportedFiles = filterDocumentsBySupport(files, false);

  const corruptedFilesNames = new Set(corruptedFiles.map((f) => f.name));
  const unsupportedNames = new Set(unsupportedFiles.map((f) => f.name));
  const corruptNameSet = new Set(corruptRemovedNames);
  const failedCorruptFiles = files.filter(
    (f) =>
      corruptNameSet.has(f.name) &&
      !corruptedFilesNames.has(f.name) &&
      !unsupportedNames.has(f.name) &&
      !fileErrors[f.name]
  );

  const renderFileNameCell = (file: File) => {
    const displayName = getFileDisplayName(file);
    return (
      <TableCell className="max-w-80 min-w-0">
        <span className="inline-block max-w-80 truncate align-middle">
          <CustomTooltip label={displayName}>{displayName}</CustomTooltip>
        </span>
      </TableCell>
    );
  };

  const renderWarningCell = (label: string) => (
    <TableCell className="text-center">
      <CustomTooltip label={label} wrapperClassName="inline-flex">
        <TriangleAlert className="size-3.5 text-danger" />
      </CustomTooltip>
    </TableCell>
  );

  return (
    <div
      style={{ display: `${visible ? "block" : "none"}` }}
      className="max-h-96"
    >
      <div className="flex w-full items-center justify-between gap-2 px-4 py-2">
        <p className="mr-4 text-base font-medium text-fg">
          {uploading
            ? `Uploading ${visibleSupportedFiles.length} files`
            : `${visibleSupportedFiles.length} uploads completed`}{" "}
        </p>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={isExpanded ? "Collapse uploads" : "Expand uploads"}
            onClick={() => dispatch(setExpanded(!isExpanded))}
          >
            {isExpanded ? <Minus /> : <ChevronUp />}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Close uploads"
            onClick={() => {
              dispatch(setVisibilty(false));
              dispatch(setFiles([]));
            }}
          >
            <X />
          </Button>
        </div>
      </div>
      <div className="relative max-h-72 overflow-auto">
        {isExpanded ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File Name</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleSupportedFiles &&
                visibleSupportedFiles?.map((file) => {
                  const progress =
                    uploadProgress[file.name] ?? (isLoading ? 0 : 100);
                  return (
                    <TableRow key={file.name}>
                      {renderFileNameCell(file)}
                      <TableCell>
                        <div className="flex items-center justify-center">
                          {progress >= 100 ? (
                            <CircleCheck className="size-3.5 text-success" />
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 text-sm text-fg-3"
                              role="progressbar"
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-valuenow={uploadProgress[file.name] || 0}
                            >
                              <Spinner className="size-3.5" />
                              {Math.round(uploadProgress[file.name] || 0)}%
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {filesWithSpecificErrors &&
                filesWithSpecificErrors?.map((file) => (
                  <TableRow key={`error-${file.name}`}>
                    {renderFileNameCell(file)}
                    {renderWarningCell(fileErrors[file.name])}
                  </TableRow>
                ))}
              {corruptedFiles &&
                corruptedFiles?.map((file) => (
                  <TableRow key={`zero-${file.name}`}>
                    {renderFileNameCell(file)}
                    {renderWarningCell("Corrupted or empty file")}
                  </TableRow>
                ))}
              {unsupportedFiles &&
                unsupportedFiles?.map((file) => (
                  <TableRow key={file.name}>
                    {renderFileNameCell(file)}
                    {renderWarningCell("Unsupported extension")}
                  </TableRow>
                ))}
              {failedCorruptFiles &&
                failedCorruptFiles?.map((file) => (
                  <TableRow key={`failed-${file.name}`}>
                    {renderFileNameCell(file)}
                    {renderWarningCell("Upload failed or unreadable file")}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        ) : null}
      </div>
    </div>
  );
};
